// frontend/src/pages/Exchange.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axiosAuth from "../axiosConfig";
import axios from "axios";
import { createChart } from "lightweight-charts";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BRAND_BLUE = "#1D4ED8";
const RED_DOWN   = "#DC2626";
const GRID       = "#E5E7EB";
const TEXT       = "#1F2937";
// Dark theme chart colors (added for dark mode; light is unchanged)
const GRID_DARK  = "#374151";
const TEXT_DARK  = "#E5E7EB";
const BG_DARK    = "#111827";

const pairToId = {
  "USDT-INR": "tether",
  "BTC-INR":  "bitcoin",
  "ETH-INR":  "ethereum",
};

export default function Exchange() {
  const navigate = useNavigate();

  const [selectedPair, setSelectedPair]   = useState("USDT-INR");
  const [timeframe, setTimeframe]         = useState("30d"); // 2h,4h,8h,7d,30d
  const [ohlcData, setOhlcData]           = useState([]);
  const [loadingChart, setLoadingChart]   = useState(true);
  const [currentPrice, setCurrentPrice]   = useState(0);

  const [orderBook, setOrderBook]         = useState({ bids: [], asks: [] });
  const [orderType, setOrderType]         = useState("MARKET");
  const [tradeSide, setTradeSide]         = useState("BUY");

  const [limitPrice, setLimitPrice]       = useState("");
  const [quantity, setQuantity]           = useState("");
  const [previewOrder, setPreviewOrder]   = useState(null);
  const [placingOrder, setPlacingOrder]   = useState(false);

  const [ordersHistory, setOrdersHistory] = useState([]);
  const [balances, setBalances]           = useState({});

  // Chart refs (create once, update data/size)
  const chartContainerRef = useRef(null);
  const chartRef          = useRef(null);
  const candleRef         = useRef(null);
  const roRef             = useRef(null);
  const themeObsRef       = useRef(null); // observe dark-mode toggles

  const orderBookIntervalRef  = useRef(null);

  // Safer base URL for trades API
  const apiRoot = (process.env.REACT_APP_API_URL || "").replace(/\/api\/auth\/?$/, "");
  const tradesAPI = useMemo(() => axios.create({
    baseURL: apiRoot ? `${apiRoot}/api/trades` : "/api/trades",
    withCredentials: true,
  }), [apiRoot]);

  // Utilities
  const fmtNum = (n, opts={ maximumFractionDigits: 6 }) => {
    try { return Number(n).toLocaleString(undefined, opts); } catch { return n; }
  };
  const shortId = (id="") => (id.length > 10 ? `${id.slice(0,5)}…${id.slice(-4)}` : id);

  // Simulate order book
  const generateOrderBook = (price) => {
    if (price <= 0) return { bids: [], asks: [] };
    const bids = [], asks = [], levels = 5, tick = price * 0.001;
    for (let i = 0; i < levels; i++) {
      bids.push({ price: +(price - i * tick).toFixed(4), amount: +(Math.random() * 100 + 1).toFixed(4) });
      asks.push({ price: +(price + 0.12 + i * tick).toFixed(4), amount: +(Math.random() * 100 + 1).toFixed(4) });
    }
    return { bids, asks };
  };

  // Fetch balances, current price, initial history whenever pair changes
  useEffect(() => {
    let mounted = true;

    axiosAuth
      .get("/whoami")
      .then((r) => axiosAuth.get(`/balance/${encodeURIComponent(r.data.user.email)}`))
      .then((r) => mounted && setBalances(r.data.balances ?? r.data ?? {}))
      .catch(() => {});

    (async () => {
      try {
        const cgId = pairToId[selectedPair];
        const resp = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${cgId}&vs_currencies=inr`);
        const json = await resp.json();
        const inrP = json[cgId]?.inr ?? 0;
        if (mounted) {
          setCurrentPrice(inrP);
          setOrderBook(generateOrderBook(inrP));
        }
      } catch {
        mounted && setCurrentPrice(0);
      }
    })();

    tradesAPI
      .get("/orders")
      .then((r) => mounted && setOrdersHistory(r.data.orders ?? []))
      .catch(() => {});

    return () => { mounted = false; };
  }, [selectedPair, tradesAPI]);

  // Live order book update
  useEffect(() => {
    clearInterval(orderBookIntervalRef.current);
    orderBookIntervalRef.current = setInterval(() => {
      setOrderBook(generateOrderBook(currentPrice));
    }, 3000);
    return () => clearInterval(orderBookIntervalRef.current);
  }, [currentPrice]);

  // Fetch OHLC data when pair/timeframe changes
  useEffect(() => {
    let mounted = true;
    setLoadingChart(true);
    (async () => {
      try {
        const coinId = pairToId[selectedPair];
        // CoinGecko OHLC days param
        const days = ["2h", "4h", "8h"].includes(timeframe) ? 1 : (timeframe === "7d" ? 7 : 30);
        const resp = await fetch(`https://api.coingecko.com/api/v3/coins/${coinId}/ohlc?vs_currency=inr&days=${days}`);
        const raw = await resp.json();
        let data = Array.isArray(raw) ? raw.map(([ts, o, h, l, c]) => ({
          time: Math.floor(ts / 1000), open: o, high: h, low: l, close: c
        })) : [];
        if (["2h", "4h", "8h"].includes(timeframe)) {
          const cutoff = Math.floor(Date.now() / 1000) - parseInt(timeframe, 10) * 3600;
          data = data.filter((pt) => pt.time >= cutoff);
        }
        mounted && setOhlcData(data);
      } catch {
        toast.error("Failed to load chart data");
        mounted && setOhlcData([]);
      } finally {
        mounted && setLoadingChart(false);
      }
    })();

    return () => { mounted = false; };
  }, [selectedPair, timeframe]);

  // Helper to apply chart theme (dark/light) without affecting light mode defaults
  const applyChartTheme = (isDark) => {
    if (!chartRef.current) return;
    try {
      chartRef.current.applyOptions({
        layout: {
          backgroundColor: isDark ? BG_DARK : "#FFFFFF",
          textColor: isDark ? TEXT_DARK : TEXT,
        },
        grid: {
          vertLines: { color: isDark ? GRID_DARK : GRID },
          horzLines: { color: isDark ? GRID_DARK : GRID },
        },
        rightPriceScale: { borderColor: isDark ? GRID_DARK : GRID },
        timeScale: { borderColor: isDark ? GRID_DARK : GRID },
      });
    } catch {}
  };

  // Create chart ONCE
  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;

    // Ensure some initial size
    const initialW = Math.max(260, Math.floor(el.clientWidth || 0));
    const initialH = Math.max(220, Math.floor(el.clientHeight || 0)) || (window.innerWidth < 768 ? 260 : 320);

    const isDarkAtMount = document.documentElement.classList.contains("dark");

    const chart = createChart(el, {
      width: initialW,
      height: initialH,
      layout: { backgroundColor: isDarkAtMount ? BG_DARK : "#FFFFFF", textColor: isDarkAtMount ? TEXT_DARK : TEXT },
      grid: { vertLines: { color: isDarkAtMount ? GRID_DARK : GRID }, horzLines: { color: isDarkAtMount ? GRID_DARK : GRID } },
      rightPriceScale: { borderColor: isDarkAtMount ? GRID_DARK : GRID },
      timeScale: { borderColor: isDarkAtMount ? GRID_DARK : GRID, timeVisible: true, secondsVisible: false },
      crosshair: { mode: 1 },
    });
    chartRef.current = chart;

    candleRef.current = chart.addCandlestickSeries({
      upColor: BRAND_BLUE,
      downColor: RED_DOWN,
      borderUpColor: BRAND_BLUE,
      borderDownColor: RED_DOWN,
      wickUpColor: BRAND_BLUE,
      wickDownColor: RED_DOWN,
    });

    // ResizeObserver: guard against nulls
    const ro = new ResizeObserver((entries) => {
      if (!entries?.[0] || !chartRef.current || !chartContainerRef.current) return;
      const { width, height } = entries[0].contentRect || {};
      if (width)  chartRef.current.applyOptions({ width: Math.max(260, Math.floor(width)) });
      if (height) chartRef.current.applyOptions({ height: Math.max(220, Math.floor(height)) });
    });
    ro.observe(el);
    roRef.current = ro;

    // Watch for dark mode toggles by observing the <html> class list
    try {
      const obs = new MutationObserver(() => {
        const nowDark = document.documentElement.classList.contains("dark");
        applyChartTheme(nowDark);
      });
      obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
      themeObsRef.current = obs;
    } catch {}

    return () => {
      try { roRef.current?.disconnect(); } catch {}
      roRef.current = null;

      try { themeObsRef.current?.disconnect(); } catch {}
      themeObsRef.current = null;

      try {
        if (candleRef.current) {
          chartRef.current?.removeSeries(candleRef.current);
          candleRef.current = null;
        }
      } catch {}
      try { chartRef.current?.remove(); } catch {}
      chartRef.current = null;
    };

  }, []);

  // Update chart data when ohlcData changes
  useEffect(() => {
    if (!candleRef.current) return;
    try {
      candleRef.current.setData(ohlcData || []);
      chartRef.current?.timeScale().fitContent();
    } catch {}
  }, [ohlcData]);

  // ----- Order preview / placement -----
  const computePreview = () => {
    setPreviewOrder(null);
    let rate = currentPrice;
    if (orderType === "LIMIT") {
      const lp = parseFloat(limitPrice);
      if (isNaN(lp) || lp <= 0) return toast.error("Enter valid limit price");
      rate = lp;
    }
    const qt = parseFloat(quantity);
    if (isNaN(qt) || qt <= 0) return toast.error("Enter valid quantity");

    if (tradeSide === "BUY") {
      const gross = qt * rate;
      const fee = gross * 0.005;
      const gst = fee * 0.18;
      const totalPayable = gross + fee + gst;
      const B = Number(balances.INR ?? 0);
      const remainingINR = B - totalPayable;
      if (remainingINR < 0) return toast.error("Insufficient INR balance");
      setPreviewOrder({ side: "BUY", rate, qty: qt, gross, fee, gst, totalPayable, remainingINR });
    } else {
      const asset = selectedPair.split("-")[0];
      const bal = Number(balances[asset] ?? 0);
      if (qt > bal) return toast.error(`Insufficient ${asset} balance`);
      const gross = qt * rate;
      const fee = gross * 0.005;
      const gst = fee * 0.18;
      const tds = gross * 0.01;
      const netINR = gross - fee - gst - tds;
      const remainingAsset = bal - qt;
      setPreviewOrder({ side: "SELL", rate, qty: qt, gross, fee, gst, tds, netINR, remainingAsset });
    }
  };

  const placeOrder = async () => {
    if (!previewOrder) return;
    setPlacingOrder(true);
    try {
      const { side, rate, qty } = previewOrder;
      const res = await tradesAPI.post("/new-order", {
        type: side, pair: selectedPair, amount: qty, price: rate,
      });
      if (res.data.success) {
        toast.success(`Order placed: ${res.data.filled ? "Filled" : "Pending"}`);
        const hist = await tradesAPI.get("/orders");
        setOrdersHistory(hist.data.orders || []);
        setQuantity(""); setLimitPrice(""); setPreviewOrder(null);
      } else {
        toast.error(res.data.message || "Order failed");
      }
    } catch {
      toast.error("Failed to place order");
    } finally {
      setPlacingOrder(false);
    }
  };

  const handleMax = () => {
    if (tradeSide === "BUY") {
      const B = Number(balances.INR ?? 0);
      const fee = B * 0.005;
      const gst = fee * 0.18;
      const net = B - fee - gst;
      const rate = orderType === "LIMIT" && !isNaN(parseFloat(limitPrice)) ? parseFloat(limitPrice) : currentPrice;
      setQuantity(((net || 0) / (rate || 1)).toFixed(6));
    } else {
      const asset = selectedPair.split("-")[0];
      setQuantity(Number(balances[asset] ?? 0).toFixed(6));
    }
    setPreviewOrder(null);
  };

  const baseCur = selectedPair.split("-")[0];

  return (
    <div className="min-h-screen bg-blue-50 dark:bg-black md:pb-10 pb-6">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-lg border-b-4 border-blue-600">
        <div className="max-w-7xl mx-auto w-full md:px-6 px-4 md:py-6 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
          <div>
            <h1 className="md:text-3xl text-2xl font-bold text-blue-800 dark:text-blue-400 mb-0.5">Exchange</h1>
            <p className="text-blue-600 dark:text-blue-300 md:text-base text-sm">Order book, chart & order placement</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto w-full md:px-6 px-3 md:mt-6 mt-3 md:space-y-8 space-y-3">
        {/* Quick Actions */}
        <motion.div
          className="bg-white dark:bg-gray-900 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-3 md:flex md:items-center md:justify-between md:gap-4 border-t-4 md:border-t-0 md:border-l-4 border-blue-500"
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-blue-700 dark:text-blue-300 font-medium md:mb-0 mb-2 text-sm md:text-base">
            Quick Actions
          </p>
          <div className="flex flex-nowrap gap-2 md:gap-3 md:ml-auto overflow-x-auto">
            <button onClick={() => navigate("/deposit")}  className="bg-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base whitespace-nowrap">Deposit</button>
            <button onClick={() => navigate("/withdraw")} className="bg-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base whitespace-nowrap">Withdraw</button>
            <button onClick={() => navigate("/quick-trade")} className="bg-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base whitespace-nowrap">Quick Trade</button>
            <button onClick={() => navigate("/p2p")}      className="bg-blue-600 text-white px-3 md:px-5 py-1.5 md:py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base whitespace-nowrap">P2P Market</button>
          </div>
        </motion.div>

        <div className="flex flex-col lg:flex-row md:gap-6 gap-4">
          {/* Order Book */}
          <div className="lg:w-1/3 bg-white dark:bg-gray-900 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-4 md:border-t-4 border-t-4 border-blue-500">
            <h2 className="md:text-xl text-lg font-semibold text-blue-800 dark:text-blue-400 md:mb-4 mb-3">
              Order Book ({selectedPair})
            </h2>
            <div className="flex md:gap-4 gap-3">
              <div className="w-1/2">
                <h3 className="text-blue-700 dark:text-blue-300 md:text-base text-sm font-medium md:mb-2 mb-1.5">Bids</h3>
                <ul className="md:space-y-1.5 space-y-1 text-blue-800 dark:text-blue-200 md:text-sm text-xs">
                  {orderBook.bids.map((b, i) => (
                    <li key={`b-${i}`} className="flex justify-between bg-green-50 dark:bg-green-900/30 md:px-3 px-2 md:py-2 py-1.5 rounded-lg">
                      <span>₹ {b.price}</span>
                      <span>{b.amount}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="w-1/2">
                <h3 className="text-blue-700 dark:text-blue-300 md:text-base text-sm font-medium md:mb-2 mb-1.5">Asks</h3>
                <ul className="md:space-y-1.5 space-y-1 text-blue-800 dark:text-blue-200 md:text-sm text-xs">
                  {orderBook.asks.map((a, i) => (
                    <li key={`a-${i}`} className="flex justify-between bg-red-50 dark:bg-red-900/30 md:px-3 px-2 md:py-2 py-1.5 rounded-lg">
                      <span>₹ {a.price}</span>
                      <span>{a.amount}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Right column: Chart + Order form + History */}
          <div className="lg:w-2/3 md:space-y-6 space-y-4">
            {/* Pair & Timeframe */}
            <div className="bg-white dark:bg-gray-900 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-4 md:border-t-4 border-t-4 border-blue-500">
              <div className="flex flex-col sm:flex-row sm:items-end sm:space-x-6 md:gap-0 gap-3">
                <div className="w-full sm:w-1/2">
                  <label className="block md:text-lg text-sm font-medium text-blue-700 dark:text-blue-300 md:mb-2 mb-1">Pair</label>
                  <select
                    value={selectedPair}
                    onChange={(e) => { setSelectedPair(e.target.value); setPreviewOrder(null); }}
                    className="w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg md:p-2 p-2 focus:outline-none focus:ring-2 focus:ring-blue-600 md:text-base text-sm"
                  >
                    <option value="USDT-INR">USDT / INR</option>
                    <option value="BTC-INR">BTC / INR</option>
                    <option value="ETH-INR">ETH / INR</option>
                  </select>
                </div>
                <div className="w-full sm:w-1/2">
                  <label className="block md:text-lg text-sm font-medium text-blue-700 dark:text-blue-300 md:mb-2 mb-1">Timeframe</label>
                  <select
                    value={timeframe}
                    onChange={(e) => setTimeframe(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg md:p-2 p-2 focus:outline-none focus:ring-2 focus:ring-blue-600 md:text-base text-sm"
                  >
                    <option value="2h">2 Hours</option>
                    <option value="4h">4 Hours</option>
                    <option value="8h">8 Hours</option>
                    <option value="7d">7 Days</option>
                    <option value="30d">30 Days</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Chart (always-mounted container + overlays) */}
            <div className="bg-white dark:bg-gray-900 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-4 p-3 md:border-t-4 border-t-4 border-blue-500">
              <div className="flex items-center justify-between md:mb-2 mb-1">
                <p className="text-blue-800 dark:text-blue-300 md:text-base text-sm">
                  Current Price:{" "}
                  <span className="font-semibold">
                    ₹ {fmtNum(currentPrice, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </p>
                <p className="md:text-xs text-[11px] text-gray-500 dark:text-gray-400">Data by CoinGecko</p>
              </div>

              <div className="relative">
                {/* Give the container a height so chart can size at mount */}
                <div ref={chartContainerRef} className="w-full h-[240px] md:h-[320px]" />
                {loadingChart && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-blue-600 dark:text-blue-300 md:text-lg text-sm">Loading chart…</p>
                  </div>
                )}
                {!loadingChart && ohlcData.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-red-600 dark:text-red-300 md:text-base text-sm">No data. Try a different timeframe.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Order Form */}
            <div className="bg-white dark:bg-gray-900 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-4 md:border-t-4 border-t-4 border-blue-500">
              <h2 className="md:text-xl text-lg font-semibold text-blue-800 dark:text-blue-400 md:mb-4 mb-3">Place Order</h2>

              <div className="md:space-y-4 space-y-3">
                {/* Side Buttons */}
                <div className="flex md:gap-4 gap-2">
                  <button
                    onClick={() => { setTradeSide("BUY"); setPreviewOrder(null); }}
                    className={`flex-1 md:py-2 py-2 rounded-lg font-medium md:text-base text-sm ${
                      tradeSide === "BUY"
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : "bg-white dark:bg-gray-800 text-gray-700 dark:text-white border border-gray-300 dark:border-gray-700"
                    } transition`}
                  >
                    Buy
                  </button>
                  <button
                    onClick={() => { setTradeSide("SELL"); setPreviewOrder(null); }}
                    className={`flex-1 md:py-2 py-2 rounded-lg font-medium md:text-base text-sm ${
                      tradeSide === "SELL"
                        ? "bg-red-600 text-white hover:bg-red-700"
                        : "bg-white dark:bg-gray-800 text-gray-700 dark:text-white border border-gray-300 dark:border-gray-700"
                    } transition`}
                  >
                    Sell
                  </button>
                </div>

                {/* Order Type & Limit Price */}
                <div className="flex flex-col sm:flex-row md:gap-4 gap-3">
                  <div className="sm:w-1/3">
                    <label className="block md:text-sm text-[13px] font-medium text-gray-700 dark:text-gray-300 md:mb-1 mb-1">Order Type</label>
                    <select
                      value={orderType}
                      onChange={(e) => { setOrderType(e.target.value); setPreviewOrder(null); }}
                      className="w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg md:p-2 p-2 focus:outline-none focus:ring-2 focus:ring-blue-600 md:text-base text-sm"
                    >
                      <option value="MARKET">Market</option>
                      <option value="LIMIT">Limit</option>
                    </select>
                  </div>

                  {orderType === "LIMIT" && (
                    <div className="sm:w-1/3">
                      <label className="block md:text-sm text-[13px] font-medium text-gray-700 dark:text-gray-300 md:mb-1 mb-1">Price (INR)</label>
                      <input
                        type="number"
                        step="0.01"
                        inputMode="decimal"
                        value={limitPrice}
                        onChange={(e) => { setLimitPrice(e.target.value); setPreviewOrder(null); }}
                        placeholder="₹ 0.00"
                        className="w-full border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-lg md:p-2 p-2 focus:outline-none focus:ring-2 focus:ring-blue-600 md:text-base text-sm"
                      />
                    </div>
                  )}
                </div>

                {/* Quantity + Max */}
                <div>
                  <label className="block md:text-sm text-[13px] font-medium text-gray-700 dark:text-gray-300 md:mb-1 mb-1">
                    Quantity ({baseCur})
                  </label>
                  <div className="flex">
                    <input
                      type="number"
                      step="0.000001"
                      inputMode="decimal"
                      value={quantity}
                      onChange={(e) => { setQuantity(e.target.value); setPreviewOrder(null); }}
                      placeholder={`0.000000 ${baseCur}`}
                      className="flex-1 border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-white rounded-l-lg md:p-2 p-2 focus:outline-none focus:ring-2 focus:ring-blue-600 md:text-base text-sm"
                    />
                    <button
                      type="button"
                      onClick={handleMax}
                      className="bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-700 border-l-0 md:px-4 px-3 rounded-r-lg hover:bg-gray-200 dark:hover:bg-gray-600 md:text-base text-sm"
                    >
                      Max
                    </button>
                  </div>
                  <p className="md:text-xs text-[11px] text-gray-600 dark:text-gray-300 md:mt-1 mt-1">
                    {tradeSide === "BUY"
                      ? `Available: ₹ ${fmtNum(Number(balances.INR ?? 0), { maximumFractionDigits: 2 })}`
                      : `Available: ${fmtNum(Number(balances[baseCur] ?? 0))} ${baseCur}`}
                  </p>
                </div>

                {/* Preview & Confirm */}
                <div className="flex md:gap-4 gap-2">
                  <button
                    onClick={computePreview}
                    className="flex-1 bg-blue-600 text-white md:py-2 py-2 rounded-lg hover:bg-blue-700 transition md:text-base text-sm"
                  >
                    Preview
                  </button>
                  {previewOrder && (
                    <button
                      onClick={placeOrder}
                      disabled={placingOrder}
                      className={`flex-1 md:py-2 py-2 rounded-lg text-white md:text-base text-sm ${
                        placingOrder
                          ? "bg-gray-400 cursor-not-allowed"
                          : tradeSide === "BUY"
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-red-600 hover:bg-red-700"
                      } transition`}
                    >
                      {placingOrder ? "Placing..." : tradeSide === "BUY" ? "Confirm Buy" : "Confirm Sell"}
                    </button>
                  )}
                </div>

                {/* Preview Details */}
                {previewOrder && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 md:p-4 p-3 rounded-lg border border-blue-200 dark:border-blue-900/40 md:mt-4 mt-3 text-blue-800 dark:text-blue-200 md:text-base text-sm">
                    {tradeSide === "BUY" ? (
                      <>
                        <p><strong>Total Payable:</strong> ₹ {fmtNum(previewOrder.totalPayable, { maximumFractionDigits: 2 })}</p>
                        <p><strong>Rate:</strong> ₹ {fmtNum(previewOrder.rate, { maximumFractionDigits: 4 })}</p>
                        <p><strong>Platform Fee (0.5%):</strong> ₹ {fmtNum(previewOrder.fee, { maximumFractionDigits: 2 })}</p>
                        <p><strong>GST (18% on fee):</strong> ₹ {fmtNum(previewOrder.gst, { maximumFractionDigits: 2 })}</p>
                        <p><strong>Remaining INR:</strong> ₹ {fmtNum(previewOrder.remainingINR, { maximumFractionDigits: 2 })}</p>
                        <p><strong>Quantity:</strong> {fmtNum(previewOrder.qty)} {baseCur}</p>
                      </>
                    ) : (
                      <>
                        <p><strong>Gross INR:</strong> ₹ {fmtNum(previewOrder.gross, { maximumFractionDigits: 2 })}</p>
                        <p><strong>Rate:</strong> ₹ {fmtNum(previewOrder.rate, { maximumFractionDigits: 4 })}</p>
                        <p><strong>Platform Fee (0.5%):</strong> ₹ {fmtNum(previewOrder.fee, { maximumFractionDigits: 2 })}</p>
                        <p><strong>GST (18% on fee):</strong> ₹ {fmtNum(previewOrder.gst, { maximumFractionDigits: 2 })}</p>
                        <p><strong>TDS (1%):</strong> ₹ {fmtNum(previewOrder.tds, { maximumFractionDigits: 2 })}</p>
                        <p><strong>Net Proceeds:</strong> ₹ {fmtNum(previewOrder.netINR, { maximumFractionDigits: 2 })}</p>
                        <p><strong>Remaining {baseCur}:</strong> {fmtNum(previewOrder.remainingAsset)}</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Order History: table on md+, cards on mobile */}
            <div className="bg-white dark:bg-gray-900 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-4 md:border-t-4 border-t-4 border-blue-500">
              <h2 className="md:text-xl text-lg font-semibold text-blue-800 dark:text-blue-400 md:mb-4 mb-3">Order History</h2>

              {/* md+ table */}
              <div className="overflow-x-auto hidden md:block">
                <table className="min-w-full table-auto text-blue-800 dark:text-blue-200 text-sm">
                  <thead>
                    <tr className="bg-blue-50 dark:bg-blue-900/20">
                      <th className="px-4 py-2 text-left font-medium">Order ID</th>
                      <th className="px-4 py-2 text-left font-medium">Pair</th>
                      <th className="px-4 py-2 text-right font-medium">Side</th>
                      <th className="px-4 py-2 text-right font-medium">Amount</th>
                      <th className="px-4 py-2 text-right font-medium">Price</th>
                      <th className="px-4 py-2 text-center font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordersHistory.length > 0 ? (
                      ordersHistory.map((o) => (
                        <tr key={o._id} className="border-b last:border-b-0 hover:bg-blue-100 dark:hover:bg-blue-900/20 transition border-gray-200 dark:border-gray-800">
                          <td className="px-4 py-3">{o._id}</td>
                          <td className="px-4 py-3">{o.pair}</td>
                          <td className={`px-4 py-3 text-right font-medium ${o.type === "buy" ? "text-green-600" : "text-red-600"}`}>
                            {o.type?.charAt(0)?.toUpperCase() + o.type?.slice(1)}
                          </td>
                          <td className="px-4 py-3 text-right">{fmtNum(o.amount)}</td>
                          <td className="px-4 py-3 text-right">{fmtNum(o.price, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                o.status === "Filled"
                                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                  : o.status === "Pending"
                                  ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300"
                                  : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                              }`}
                            >
                              {o.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center text-blue-500 dark:text-blue-300">No orders to display.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="space-y-3 md:hidden">
                {ordersHistory.length > 0 ? (
                  ordersHistory.map((o) => (
                    <div key={o._id} className="border rounded-lg p-3 bg-blue-50/40 dark:bg-blue-900/20 border-blue-100 dark:border-blue-900/40">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] text-gray-500 dark:text-gray-400">#{shortId(o._id)}</span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                            o.status === "Filled"
                              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                              : o.status === "Pending"
                              ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300"
                              : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                          }`}
                        >
                          {o.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">{o.pair}</p>
                          <p className={`text-xs ${o.type === "buy" ? "text-green-600" : "text-red-600"}`}>
                            {o.type?.toUpperCase()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-blue-900 dark:text-blue-200">Amt: {fmtNum(o.amount)}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-300">₹ {fmtNum(o.price, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-blue-500 dark:text-blue-300 text-sm py-4">No orders to display.</div>
                )}
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
}
